'use client'

import React from 'react'
import type { Media } from '@/payload-types'
import Image from 'next/image'
import { getMediaUrl } from '@/utilities/getMediaUrl'
import { CMSLink } from '@/components/Link'
import type { LinkFieldType } from '@/fields/link'
import { PlusIcon } from '@/components/icons/PlusIcon'
import { MinusIcon } from '@/components/icons/MinusIcon'
import { ShareIcon } from '@/components/icons/ShareIcon'

type CardStyleRevealProps = {
  title: string
  backgroundImage?: Media | string | null
  description?: string
  cta?: LinkFieldType
  isExpanded?: boolean
  onToggle?: () => void
}

export const CardStyleReveal: React.FC<CardStyleRevealProps> = ({
  title,
  backgroundImage,
  description,
  cta,
  isExpanded = false,
  onToggle,
}) => {
  // Check if CTA is valid (Payload returns empty object {} for unfilled link fields)
  const hasValidCta = cta && (cta.url || cta.reference)
  const { appearance: _ctaAppearance, ...ctaLinkProps } = cta || {}

  // Get background image URL and metadata
  let backgroundUrl: string | null = null
  let backgroundAlt = ''

  if (backgroundImage && typeof backgroundImage === 'object') {
    backgroundUrl = getMediaUrl(backgroundImage.url, backgroundImage.updatedAt)
    backgroundAlt = backgroundImage.alt || title
  } else if (typeof backgroundImage === 'string') {
    backgroundUrl = backgroundImage
  }

  // When a background image exists, the card fades to transparent on hover to let the image show;
  // otherwise it switches to the sub-brand's dark color (from CSS custom property).
  const hoverBgClass = backgroundUrl ? 'hover:bg-transparent' : 'hover:bg-brand-dark'
  const hoverTextClass = backgroundUrl ? 'group-hover:text-white' : ''
  const descriptionTextClass = backgroundUrl
    ? 'text-black group-hover:text-white'
    : 'text-black'

  // Desktop card content
  const desktopCard = (
    <div
      className={`group hidden lg:flex flex-col justify-end p-7 relative rounded-[16px] w-full min-h-[416px] overflow-hidden isolate cursor-pointer bg-civo-cloud ${hoverBgClass} transition-colors duration-300 ease-in-out`}
    >
      {/* Background Image - only visible on hover */}
      {backgroundUrl && (
        <div className="absolute inset-0 z-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <Image
            src={backgroundUrl}
            alt={backgroundAlt}
            fill
            className="object-cover"
            sizes="(max-width: 1024px) 100vw, 33vw"
          />
          {/* Gradient overlay for text visibility */}
          <div className="absolute inset-0 bg-gradient-to-b from-[rgba(0,0,0,0.2)] to-[rgba(0,0,0,0.75)]" />
        </div>
      )}

      {/* Content at bottom */}
      <div className="relative z-10 flex flex-col w-full">
        {/* Title - text turns white on hover only if there's a background image */}
        <div
          className={`subheading-large transition-colors duration-300 ease-in-out text-black ${hoverTextClass}`}
        >
          <p className="mb-0">{title}</p>
        </div>

        {/* Description - reveals on hover via grid-rows transition */}
        {description && (
          <div className="grid grid-rows-[0fr] group-hover:grid-rows-[1fr] transition-[grid-template-rows] duration-300 ease-in-out">
            <div className="overflow-hidden">
              <div
                className={`body-medium pt-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300 ${descriptionTextClass}`}
              >
                <p className="mb-0">{description}</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Plus/Minus/Arrow icon - always visible, changes on hover with scale animation */}
      <div className="absolute top-7 right-7 z-10">
        <div className="relative size-[45px] rounded-full overflow-hidden bg-civo-lime group-hover:bg-black transition-colors duration-300 ease-out">
          {/* Black overlay that scales up on hover - parent bg also transitions to black so no lime bleeds through at the edge */}
          <div className="absolute inset-0 rounded-full bg-black scale-0 group-hover:scale-100 transition-transform duration-300 ease-out" />
          {/* Stacked icons - swap via opacity on hover */}
          <div className="absolute inset-0 z-10">
            <div className="absolute inset-0 flex items-center justify-center opacity-100 group-hover:opacity-0 transition-opacity duration-300">
              <PlusIcon />
            </div>
            <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
              {hasValidCta ? <ShareIcon /> : <MinusIcon />}
            </div>
          </div>
        </div>
      </div>
    </div>
  )

  // Mobile card - single component with animated transitions (prop-driven, not hover)
  const mobileCard = (
    <div
      className="lg:hidden flex flex-col p-6 relative rounded-[16px] w-full overflow-hidden isolate cursor-pointer transition-all duration-300 ease-in-out"
      style={{
        backgroundColor: isExpanded
          ? backgroundUrl
            ? 'transparent'
            : 'var(--brand-color-dark)'
          : '#D1CCBE',
        minHeight: isExpanded ? '230px' : '80px',
      }}
      onClick={!isExpanded || !hasValidCta ? onToggle : undefined}
    >
      {/* Background Image with Gradient Overlay - only visible when expanded */}
      {backgroundUrl && (
        <div
          className="absolute inset-0 z-0 transition-opacity duration-300"
          style={{ opacity: isExpanded ? 1 : 0 }}
        >
          <Image
            src={backgroundUrl}
            alt={backgroundAlt}
            fill
            className="object-cover"
            sizes="100vw"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[rgba(0,0,0,0.2)] to-[rgba(0,0,0,0.75)]" />
        </div>
      )}

      {/* Content */}
      <div className="relative z-10 flex flex-col justify-between h-full w-full flex-1">
        {/* Title and icon row */}
        <div className="flex items-start justify-between gap-2 w-full">
          <div
            className="display-x-small flex-1 min-w-0 transition-colors duration-300"
            style={{ color: isExpanded && backgroundUrl ? 'white' : 'black' }}
          >
            <p className="mb-0">{title}</p>
          </div>
          {/* Icon with scale animation */}
          <div
            className="relative shrink-0 size-[45px]"
            onClick={(e) => {
              e.stopPropagation()
              onToggle?.()
            }}
          >
            <div className="relative size-[45px] rounded-full overflow-hidden bg-civo-lime">
              {/* Black overlay - scales based on isExpanded */}
              <div
                className="absolute inset-0 rounded-full transition-transform duration-300 ease-out bg-black"
                style={{ transform: isExpanded ? 'scale(1)' : 'scale(0)' }}
              />
              {/* Icon container */}
              <div className="absolute inset-0 flex items-center justify-center z-10">
                {isExpanded ? (
                  hasValidCta ? (
                    <ShareIcon />
                  ) : (
                    <MinusIcon />
                  )
                ) : (
                  <PlusIcon />
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Description - reveals when expanded */}
        {description && (
          <div
            className="body-copy-medium w-full overflow-hidden transition-all duration-300 ease-in-out"
            style={{
              maxHeight: isExpanded ? '200px' : '0',
              marginTop: isExpanded ? '16px' : '0',
              opacity: isExpanded ? 1 : 0,
              color: isExpanded && backgroundUrl ? 'white' : 'black',
            }}
          >
            <p className="mb-0">{description}</p>
          </div>
        )}
      </div>
    </div>
  )

  return (
    <div className="w-full lg:flex-1">
      {/* Desktop: Wrap in CMSLink only if CTA exists */}
      {hasValidCta ? (
        <CMSLink {...ctaLinkProps} label={undefined} appearance="inline" className="hidden lg:block">
          {desktopCard}
        </CMSLink>
      ) : (
        desktopCard
      )}

      {/* Mobile: Wrap in CMSLink when expanded with valid CTA */}
      {isExpanded && hasValidCta ? (
        <CMSLink {...ctaLinkProps} label={undefined} appearance="inline" className="block lg:hidden">
          {mobileCard}
        </CMSLink>
      ) : (
        mobileCard
      )}
    </div>
  )
}
